import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { Avatar, Button, Form, Input } from "antd";
import ModalComponent from "../../../components/modals/ModalComponent";
import LoadableButton from "../../../components/buttons/LoadableButton";
import { ChangeClientPasswordApi } from "../../../api/request/auth";
import Eye from "../../../assets/Eye";
import { VerifyPasswordApi } from "../../../api/request/settings/settings";
import UpdateProfileModal from "./UpdateProfileModal";

const Information = ({ client, setClient }) => {
  const [form] = Form.useForm();
  const [authForm] = Form.useForm();
  const userDetails = useSelector((state) => state.user.userDetails);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(true);
  const [initialValues, setInitialValues] = useState({
    new_password: "",
    confirm_password: "",
  });
  const [isAuthentication, setIsAuthentication] = useState(false);
  const [isAuthenticationLoading, setIsAuthenticationLoading] = useState(false);
  const [clientSin, setClientSin] = useState(client?.sin_number);
  const [isUpdateProfileModal, setIsUpdateProfileModal] = useState(false);

  const getInitials = (name) => {
    const nameParts = name?.trim()?.split(" ");
    if (nameParts) {
      const initials = nameParts[0][0] + nameParts[nameParts.length - 1][0];
      return initials.toUpperCase();
    }
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    delete values?.confirm_password;
    const params = {
      id: client.id,
      ...values,
    };
    try {
      const { data } = await ChangeClientPasswordApi(params);
      if (data?.status) {
        toast.success(data?.message);
        setIsResetPassword(false);
      } else {
        toast.error(data?.message);
      }
      setIsLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (client?.sin_number) setClientSin(client?.sin_number);
  }, [client]);

  useEffect(() => {
    if (!isResetPassword) {
      form.resetFields();
      setInitialValues({ new_password: "", confirm_password: "" });
    }
  }, [isResetPassword]);

  const verifyPassword = async (values) => {
    try {
      setIsAuthenticationLoading(true);
      const params = { user_id: client?.id, ...values };
      const { data } = await VerifyPasswordApi(params);
      if (data?.status) {
        setClientSin(data?.data);
        authForm.resetFields();
        toast.success(data?.message);
        setIsAuthentication(false);
      }
      setIsAuthenticationLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setIsAuthenticationLoading(false);
    }
  };

  return (
    <div>
      <div className="grid md:grid-cols-3 gap-5 m-4">
        <div className="md:col-span-2 border-2 rounded-lg">
          <div className="p-4 flex items-center justify-between">
            <p className="text-lg font-semibold">Account details</p>
            <Button
              size="large"
              type="primary"
              className="p-4 uppercase font-semibold"
              onClick={(e) => setIsResetPassword(true)}
            >
              Reset password
            </Button>
          </div>
          <ModalComponent
            isOpen={isResetPassword}
            setIsOpen={setIsResetPassword}
            title="Reset Password"
            width={450}
          >
            <h4 className="text-base font-medium">
              Please fill in the fields below to proceed with updating your
              password
            </h4>
            <Form
              autoComplete="off"
              form={form}
              initialValues={initialValues}
              onFinish={handleSubmit}
            >
              <Form.Item
                name="new_password"
                className="mb-0 mt-5"
                rules={[
                  {
                    required: true,
                    message: "Please enter your new password!",
                  },
                  {
                    pattern:
                      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/,
                    message: "Please enter strong password.",
                  },
                ]}
              >
                <Input.Password
                  placeholder="New password"
                  size="large"
                  visibilityToggle={{
                    visible: passwordVisible,
                    onVisibleChange: setPasswordVisible,
                  }}
                />
              </Form.Item>
              <Form.Item
                name="confirm_password"
                className="mb-0 mt-5"
                rules={[
                  {
                    required: true,
                    message: "Please enter your confirm password!",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("new_password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          "The new password that you entered do not match!"
                        )
                      );
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm password" size="large" />
              </Form.Item>
              <LoadableButton
                className="bg-primary text-white uppercase text-base font-semibold w-full py-2 rounded-lg mt-7 mb-5"
                type="submit"
                lable="Change password"
                loadingLable="UpDating password..."
                isLoading={isLoading}
              />
            </Form>
          </ModalComponent>
          <div className="border-y-2 mx-4 mb-4 flex items-center gap-5 md:p-4">
            <div className="flex items-center gap-6">
              <div className="relative">
                <Avatar
                  size="large"
                  className="uppercase size-32 text-4xl"
                  src={client?.image}
                >
                  {getInitials(client?.name)}
                </Avatar>
                <button
                  className="bg-primary p-2 rounded-full cursor-pointer absolute bottom-1 right-2"
                  onClick={() => setIsUpdateProfileModal(true)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="white"
                    class="size-5"
                  >
                    <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32L19.513 8.2Z" />
                  </svg>
                </button>
              </div>
            </div>
            <div>
              <p className="text-lg font-semibold capitalize">{client?.name}</p>
              <p className="text-secondaryLight2 capitalize">
                {client?.account_type}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-12 mx-4 mb-4 md:px-4">
            <div className="w-full flex flex-col gap-2">
              <div className="flex gap-5">
                <p className="font-medium w-28">Email</p>
                <p>{client?.email || "--"}</p>
              </div>
              <div className="flex gap-5">
                  <p className="font-medium w-28">Spouse Email</p>
                  <p>{client?.spouse_email || "--"}</p>
              </div>
              <div className="flex gap-5">
                <p className="font-medium w-28">Phone 1</p>
                <p>{client?.mobile_no || "--"}</p>
              </div>
              <div className="flex gap-5">
                <p className="font-medium w-28">Phone 2</p>
                <p>{client?.mobile_no2 || "--"}</p>
              </div>
              <div className="flex items-center gap-5">
                <p className="font-medium w-28">SIN/BIN</p>
                <p>
                  {clientSin ? clientSin : clientSin === null ? "--" : "*****"}
                </p>
                {userDetails?.role === "employee" && !clientSin && (
                  <div
                    className="cursor-pointer"
                    onClick={() => setIsAuthentication(true)}
                  >
                    <Eye />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border-2 rounded-lg">
          <div className="p-4">
            <p className="text-lg font-semibold">Client Download Mobile App</p>
          </div>

          <div className="border-t-2 mx-4 mb-4 pt-4 flex items-center gap-5 flex-wrap">
            <button className="h-10">
              <img
                src="/assets/icons/playstore-btn.svg"
                alt="btn"
                className="object-contain"
              />
            </button>
            <button className="h-10">
              <img
                src="/assets/icons/appstore-btn.svg"
                alt="btn"
                className="object-contain"
              />
            </button>
          </div>
        </div>
      </div>

      <ModalComponent
        isOpen={isAuthentication}
        setIsOpen={setIsAuthentication}
        title={"Authentication"}
      >
        <Form
          autoComplete="off"
          form={authForm}
          initialValues={{ password: "" }}
          onFinish={verifyPassword}
        >
          <Form.Item
            name="password"
            className="mb-0 mt-5"
            rules={[{ required: true, message: "Please enter your password!" }]}
          >
            <Input.Password placeholder="Password" size="large" />
          </Form.Item>
          <div className="flex justify-center items-center mt-7">
            <LoadableButton
              className="bg-primary text-white uppercase text-base font-semibold px-5 py-2 rounded-lg"
              type="submit"
              lable="Submit"
              loadingLable="Submitting..."
              isLoading={isAuthenticationLoading}
            />
          </div>
        </Form>
      </ModalComponent>

      <UpdateProfileModal
        client={client}
        setClient={setClient}
        isOpen={isUpdateProfileModal}
        setIsOpen={setIsUpdateProfileModal}
      />
    </div>
  );
}

export default Information;
